import React from 'react';
import { Button, Col, Drawer, Row, Table, Tag } from 'antd';
import { useState, useEffect } from 'react';
import PageTitle from 'shared_components/page_title';
import DownloadCSV from 'shared_components/download_csv';
import { fetchUnitOfMeasureItems, applySorting } from 'services/settings/lookup_table/unitOfMeasure.jsx';
import UnitOfMeasureForm from './unitOfMeasureForm.jsx';
import { DndProvider } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import update from 'immutability-helper';
import DragableBodyRow from 'shared_components/drag_and_drop_row';
import { showMessage } from 'services/settings/message';

function UnitOfMeasure() {
	const [unitOfMeasure, setUnitOfMeasure] = useState([]);
	const [hasReordered, setHasReordered] = useState(false);
	const [drawerState, setDrawerState] = useState({
		visible: false,
		drawerTitle: '',
		selecetedData: null,
		buttonNames: '',
	});

	const getUnitOfMeasure = async () => {
		const data = await fetchUnitOfMeasureItems();
		// Sort by unitOfMeasureSort ASC, but put records without unitOfMeasureSort at the end
		const sortedData = (data || []).sort((a, b) => {
			if (a.unitOfMeasureSort != null && b.unitOfMeasureSort != null) {
				return a.unitOfMeasureSort - b.unitOfMeasureSort;
			}
			if (a.unitOfMeasureSort == null) return 1;
			if (b.unitOfMeasureSort == null) return -1;
			return 0;
		});
		setUnitOfMeasure(sortedData);
	};

	useEffect(() => {
		getUnitOfMeasure();
	}, []);

	const onSuccessCreate = async () => {
		onDrawerClose();
		await getUnitOfMeasure();
		showMessage('M0002');
	};

	const onSuccessUpdate = async () => {
		onDrawerClose();
		await getUnitOfMeasure();
	};

	const applySort = async () => {
		const dataPayload = unitOfMeasure.map((rec, index) => {
			return {
				unitOfMeasureID: rec.unitOfMeasureID,
				unitOfMeasureSort: index + 1,
			};
		});

		await applySorting(dataPayload);
		setHasReordered(false);
	};

	const moveRow = (dragIndex, hoverIndex) => {
		const dragRow = unitOfMeasure[dragIndex];

		const updatedData = update({ unitOfMeasure }, {
			unitOfMeasure: { $splice: [[dragIndex, 1], [hoverIndex, 0, dragRow]] }
		});

		setUnitOfMeasure(updatedData.unitOfMeasure);
		setHasReordered(true);
	};

	const showDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'ADD UNIT OF MEASURE',
			selecetedData: null,
			buttonNames: 'ADD',
		});
	};

	const displayDrawer = record => {
		setDrawerState({
			visible: true,
			drawerTitle: 'UPDATE UNIT OF MEASURE',
			selecetedData: record,
			buttonNames: 'UPDATE',
		});
	};

	const onDrawerClose = () => {
		setDrawerState({
			visible: false,
			drawerTitle: '',
			selecetedData: null,
			buttonNames: '',
		});
	};

	const components = {
		body: {
			row: DragableBodyRow,
		},
	};

	const columns = [
		{
			title: 'UNIT CODE',
			dataIndex: 'unitOfMeasureCode',
			width: 150,
			sorter: (a, b) => { return a.unitOfMeasureCode.localeCompare(b.unitOfMeasureCode) },
			sortDirections: ['ascend', 'descend'],
		},
		{
			title: 'UNIT BASE',
			dataIndex: 'unitOfMesureBase',
			width: 150,
		},
		{
			title: 'UNIT FROM',
			dataIndex: 'unitOfMeasureFrom',
			width: 150,
		},
		{
			title: 'OPERATOR',
			dataIndex: 'unitOfMeasureOperator',
			width: 100,
		},
		{
			title: 'FACTOR',
			dataIndex: 'unitOfMeasureFactor',
			width: 100,
		},
		{
			title: 'DATE CREATED',
			dataIndex: 'createdDate',
			width: 150,
			render: date => date ? new Date(date).toLocaleDateString() : '',
		},
		{
			title: 'STATUS',
			dataIndex: 'isActive',
			width: 100,
			render: active => (
				<Tag color={active === 0 ? 'red' : 'green'}>{active === 0 ? 'INACTIVE' : 'ACTIVE'}</Tag>
			),
		},
	];

	return (
		<>
			<Row justify="end" style={{ marginTop: 12, marginBottom: 12, display: 'flex', gap: '10px' }}>
				<DownloadCSV
					confirmRequired={true}
					isLookupTable={true}
					confirmDetails={{
						section: 'UNITS OF MEASURES',
						count: unitOfMeasure.length
					}}
					data={unitOfMeasure}
					filename="units_of_measures"
					columns={[
						{ title: 'UNIT CODE', dataIndex: 'unitOfMeasureCode' },
						{ title: 'UNIT BASE', dataIndex: 'unitOfMesureBase' },
						{ title: 'UNIT FROM', dataIndex: 'unitOfMeasureFrom' },
						{ title: 'OPERATOR', dataIndex: 'unitOfMeasureOperator' },
						{ title: 'FACTOR', dataIndex: 'unitOfMeasureFactor' },
						{ 
							title: 'DATE CREATED', 
							dataIndex: 'createdDate',
							render: date => date ? new Date(date).toLocaleDateString() : ''
						},
						{ 
							title: 'STATUS', 
							dataIndex: 'isActive',
							render: active => active === 0 ? 'INACTIVE' : 'ACTIVE'
						}
					]}
				/>
				<Button onClick={applySort} shape="round" type="primary" disabled={!hasReordered}>
					APPLY SORTING
				</Button>
				<Button
					// disabled={buttonDisable}
					type="primary"
					shape="round"
					onClick={showDrawer}
					style={{ marginRight: '15px' }}
					// icon={<PlusOutlined />}
				>
					ADD UNIT OF MEASURES
				</Button>
				{/* <TablePager handleChange={this.handleChange} /> */}
			</Row>
			<DndProvider backend={HTML5Backend}>
				<Table
					dataSource={unitOfMeasure}
					columns={columns}
					components={components}
					pagination={false}
					rowKey={record => record.unitOfMeasureID}
					onRow={(record, index) => ({
						index: unitOfMeasure.findIndex(item => item.unitOfMeasureID === record.unitOfMeasureID),
						moveRow: moveRow,
						onDoubleClick: () => {
							displayDrawer(record);
						},
					})}
				/>
			</DndProvider>
			<Drawer
				title={drawerState.drawerTitle}
				width="30%"
				visible={drawerState.visible}
				onClose={onDrawerClose}
				destroyOnClose
			>
				<UnitOfMeasureForm
					buttonNames={drawerState.buttonNames}
					onDrawerClose={onDrawerClose}
					selecetedData={drawerState.selecetedData}
					onSuccessCreate={onSuccessCreate}
					onSuccessUpdate={onSuccessUpdate}
					maxUnitOfMeasureSort={Math.max(...unitOfMeasure.map(u => u.unitOfMeasureSort || 0), 0)}
				/>
			</Drawer>
		</>
	);
}

export default UnitOfMeasure;
